/**
 * User context utilities for auto-populating forms.
 * This file provides types and a pure function to extract user information from a JWT.
 */
import { DutyLogClass, DutyLogContext } from '../types/user';
import { EM3_ROLES } from '../types/identity';

export interface UserContextInfo {
  userName: string;
  userClass: DutyLogClass;
  userContext: DutyLogContext;
}

/**
 * Default values for user context when information is not available.
 */
export const DEFAULT_USER_CONTEXT: UserContextInfo = {
  userName: 'Loading...',
  userClass: 'HQ',
  userContext: 'RDO'
};

/**
 * Extracts user information (name, class, context) from the FusionAuth userInfo object.
 * This is a pure function with no React hooks.
 * @param userInfo - The userInfo object from the useFusionAuth hook.
 * @returns A UserContextInfo object. If userInfo is missing, it returns defaults.
 */
export const extractUserInfo = (userInfo: any | null | undefined): UserContextInfo => {
  if (!userInfo) {
    console.warn("No userInfo provided to extractUserInfo. Using defaults.");
    return {
      userName: 'John Smith', // A more realistic default for a logged-out state
      userClass: 'HQ',
      userContext: 'RDO'
    };
  }

  try {
    // console.log('[USER-DEBUG] FusionAuth userInfo for user context:', userInfo);

    const context: Partial<UserContextInfo> = {};

    // Extract user name
    if (userInfo.given_name && userInfo.family_name) {
      context.userName = `${userInfo.given_name} ${userInfo.family_name}`;
    } else if (userInfo.name) {
      context.userName = userInfo.name;
    } else if (userInfo.preferred_username) {
      context.userName = userInfo.preferred_username;
    } else if (userInfo.email) {
      context.userName = userInfo.email.split('@')[0];
    }

    // Extract region/class from roles. Order doesn't matter — a user
    // should only ever hold one region role. First match wins.
    const regionRoles: DutyLogClass[] = [
      EM3_ROLES.HQ, EM3_ROLES.INUVIK, EM3_ROLES.SOUTH_SLAVE,
      EM3_ROLES.SAHTU, EM3_ROLES.DEH_CHO, EM3_ROLES.NORTH_SLAVE,
    ] as DutyLogClass[];
    for (const region of regionRoles) {
      if (userInfo.roles?.includes(region)) {
        context.userClass = region;
        break;
      }
    }

    // Extract duty context from roles. TDO outranks RDO when both
    // are present (shouldn't happen, but be deterministic).
    if (userInfo.roles?.includes(EM3_ROLES.TDO)) {
      context.userContext = 'TDO';
    } else if (userInfo.roles?.includes(EM3_ROLES.RDO)) {
      context.userContext = 'RDO';
    } else if (userInfo.roles?.includes(EM3_ROLES.ADMIN)) {
      context.userContext = 'ADMIN';
    }

    // Merge extracted info with defaults
    return {
      userName: context.userName || 'John Smith',
      userClass: context.userClass || 'HQ',
      userContext: context.userContext || 'RDO'
    };

  } catch (error) {
    console.error('Error extracting user info from userInfo object:', error);
    return DEFAULT_USER_CONTEXT;
  }
};
